import type {
  WidgetCell as Cell,
  WidgetSeriesRow as SeriesRow,
  WidgetDeclaration,
} from '@crowdex/plugin-contracts';
import { useEffect, useRef, useState } from 'react';
import { columnTitle, formatCell, formatPeriod, formatValue, type ValueFormat } from './format';

/**
 * Calm charts: marks in the theme's ink, a few steps of it for series, recessive axes. At most
 * this many series get a step; the others fold into "Other".
 */
const MAX_SERIES = 4;
const OTHER = 'Other';

export type SeriesModel = {
  periods: string[];
  /** Largest first: the legend's order, and the ink's (darkest for the largest). */
  groups: string[];
  slot: (group: string) => number;
  value: (period: string, group: string) => number;
  monthly: boolean;
};

/** Rows as periods × groups, the smallest groups folded into "Other". */
export function seriesModel(rows: SeriesRow[]): SeriesModel {
  const totals = new Map<string, number>();
  for (const row of rows)
    totals.set(row.group ?? '', (totals.get(row.group ?? '') ?? 0) + row.value);
  const ranked = [...totals.entries()].toSorted((a, b) => b[1] - a[1]).map(([group]) => group);
  const kept = new Set(ranked.length > MAX_SERIES + 1 ? ranked.slice(0, MAX_SERIES) : ranked);
  const cells = new Map<string, number>();
  for (const row of rows) {
    const group = kept.has(row.group ?? '') ? (row.group ?? '') : OTHER;
    const key = `${row.at ?? ''}\n${group}`;
    cells.set(key, (cells.get(key) ?? 0) + row.value);
  }
  const groups = [...kept, ...(kept.size < ranked.length ? [OTHER] : [])];
  const periods = [...new Set(rows.map((row) => row.at ?? ''))].toSorted();
  const days =
    periods.length > 1 ? (Date.parse(periods[1] ?? '') - Date.parse(periods[0] ?? '')) / 864e5 : 0;
  return {
    periods,
    groups,
    slot: (group) => (group === OTHER ? 5 : Math.min(groups.indexOf(group), MAX_SERIES - 1) + 1),
    value: (period, group) => cells.get(`${period}\n${group}`) ?? 0,
    monthly: days >= 28,
  };
}

/** Clean axis ticks from 0 to above the largest value. */
export function ticks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const rough = max / 3;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step =
    [1, 2, 2.5, 5, 10].map((factor) => factor * power).find((candidate) => candidate >= rough) ??
    rough;
  const result: number[] = [];
  for (let value = 0; value < max + step; value += step) result.push(value);
  return result;
}

/** A monotone cubic path through points (Fritsch–Carlson): no overshoot, flat stays flat. */
export function smoothPath(points: [number, number][]): string {
  const n = points.length;
  const [first] = points;
  if (first === undefined) return '';
  if (n === 1) return `M${first[0]},${first[1]}`;
  const dx: number[] = [];
  const slopes: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    const [x0, y0] = points[i] ?? first;
    const [x1, y1] = points[i + 1] ?? first;
    dx.push(x1 - x0);
    slopes.push((y1 - y0) / (x1 - x0 || 1));
  }
  const tangents = points.map((_, i) => {
    if (i === 0) return slopes[0] ?? 0;
    if (i === n - 1) return slopes[n - 2] ?? 0;
    const before = slopes[i - 1] ?? 0;
    const after = slopes[i] ?? 0;
    if (before * after <= 0) return 0;
    const h0 = dx[i - 1] ?? 1;
    const h1 = dx[i] ?? 1;
    return (3 * (h0 + h1)) / ((2 * h1 + h0) / before + (h1 + 2 * h0) / after);
  });
  let path = `M${first[0]},${first[1]}`;
  for (let i = 0; i < n - 1; i += 1) {
    const [x0, y0] = points[i] ?? first;
    const [x1, y1] = points[i + 1] ?? first;
    const third = (dx[i] ?? 0) / 3;
    path += `C${x0 + third},${y0 + third * (tangents[i] ?? 0)} ${x1 - third},${y1 - third * (tangents[i + 1] ?? 0)} ${x1},${y1}`;
  }
  return path;
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const measure = () => setWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    // A view mounted while its frame was hidden (0 wide) is measured again when the frame shows.
    window.addEventListener('resize', measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  return [ref, width] as const;
}

function Legend({ model }: { model: SeriesModel }) {
  if (model.groups.length < 2) return null;
  return (
    <ul className="widget-legend">
      {model.groups.map((group) => (
        <li key={group}>
          <span
            className="widget-swatch"
            style={{ background: `var(--series-${model.slot(group)})` }}
          />
          {group || '—'}
        </li>
      ))}
    </ul>
  );
}

/** The period under the pointer; its tooltip opens towards the plot's wider side. */
type Hover = { index: number; x: number; flip: boolean } | null;

const HEIGHT = 190;
const PAD = { top: 8, right: 40, bottom: 22, left: 4 };

function Tooltip({
  hover,
  model,
  format,
}: {
  hover: Hover;
  model: SeriesModel;
  format: ValueFormat;
}) {
  if (hover === null) return null;
  const period = model.periods[hover.index] ?? '';
  const single = model.groups.length < 2;
  return (
    <div
      className="widget-tooltip"
      data-flip={hover.flip || undefined}
      role="status"
      style={{ left: hover.x, top: PAD.top }}
    >
      {period && (
        <span className="widget-tooltip-period">{formatPeriod(period, model.monthly)}</span>
      )}
      {model.groups.map((group) => (
        <span key={group} className="widget-tooltip-row">
          {!single && (
            <i
              className="widget-swatch"
              style={{ background: `var(--series-${model.slot(group)})` }}
            />
          )}
          {!single && <em>{group || '—'}</em>}
          <b>{formatValue(model.value(period, group), format, false)}</b>
        </span>
      ))}
    </div>
  );
}

/** A plot's frame: faint ticks on the right, a few period labels below. */
function Frame({
  width,
  model,
  format,
  scale,
  x,
  children,
}: {
  width: number;
  model: SeriesModel;
  format: ValueFormat;
  scale: number[];
  x: (index: number) => number;
  children: React.ReactNode;
}) {
  const max = scale.at(-1) ?? 1;
  const y = (value: number) => PAD.top + (HEIGHT - PAD.top - PAD.bottom) * (1 - value / max);
  const every = Math.max(1, Math.ceil(model.periods.length / Math.max(1, Math.floor(width / 80))));
  const last = model.periods.length - 1;
  // Every few periods, and the last one, unless it would crowd the label before it.
  const labelled = (index: number) =>
    index === last || (index % every === 0 && last - index >= every);
  return (
    <svg aria-hidden="true" className="widget-svg" height={HEIGHT} width={width}>
      {scale.map((tick) => (
        <g key={tick}>
          <line
            className={tick === 0 ? 'widget-baseline' : 'widget-gridline'}
            x1={PAD.left}
            x2={width - PAD.right}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text className="widget-axis" x={width - PAD.right + 8} y={y(tick) + 3.5}>
            {formatValue(tick, format)}
          </text>
        </g>
      ))}
      {model.periods.map((period, index) =>
        period && labelled(index) ? (
          <text
            key={period}
            className="widget-axis"
            textAnchor={index === 0 ? 'start' : index === last ? 'end' : 'middle'}
            x={x(index)}
            y={HEIGHT - 5}
          >
            {formatPeriod(period, model.monthly)}
          </text>
        ) : null,
      )}
      {children}
    </svg>
  );
}

/** Values over time: a smooth line per group (a flat fill under it when alone). */
export function LineChart({ rows, format }: { rows: SeriesRow[]; format: ValueFormat }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<Hover>(null);
  const model = seriesModel(rows);
  const max = Math.max(
    0,
    ...model.periods.flatMap((period) => model.groups.map((group) => model.value(period, group))),
  );
  const scale = ticks(max);
  const top = scale.at(-1) ?? 1;
  const inner = Math.max(1, width - PAD.left - PAD.right);
  const x = (index: number) =>
    PAD.left +
    (model.periods.length < 2 ? inner / 2 : (inner * index) / (model.periods.length - 1));
  const y = (value: number) => PAD.top + (HEIGHT - PAD.top - PAD.bottom) * (1 - value / top);
  const onMove = (event: React.PointerEvent) => {
    const box = event.currentTarget.getBoundingClientRect();
    const position = event.clientX - box.left;
    const index =
      model.periods.length < 2
        ? 0
        : Math.round(((position - PAD.left) / inner) * (model.periods.length - 1));
    const clamped = Math.min(model.periods.length - 1, Math.max(0, index));
    setHover({ index: clamped, x: x(clamped), flip: x(clamped) > width / 2 });
  };
  const single = model.groups.length === 1;
  const end = hover?.index ?? model.periods.length - 1;
  return (
    <div className="widget-chart">
      <div
        ref={ref}
        className="widget-plot"
        onPointerLeave={() => setHover(null)}
        onPointerMove={onMove}
      >
        {width > 0 && (
          <Frame format={format} model={model} scale={scale} width={width} x={x}>
            {hover !== null && (
              <line
                className="widget-crosshair"
                x1={x(hover.index)}
                x2={x(hover.index)}
                y1={PAD.top}
                y2={HEIGHT - PAD.bottom}
              />
            )}
            {model.groups.map((group) => {
              const points = model.periods.map((period, index): [number, number] => [
                x(index),
                y(model.value(period, group)),
              ]);
              const path = smoothPath(points);
              const color = `var(--series-${model.slot(group)})`;
              return (
                <g key={group}>
                  {single && (
                    <path
                      d={`${path}L${x(model.periods.length - 1)},${y(0)}L${x(0)},${y(0)}Z`}
                      fill="var(--series-1)"
                      fillOpacity={0.08}
                    />
                  )}
                  <path
                    d={path}
                    fill="none"
                    stroke={color}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                  <circle
                    className="widget-dot"
                    cx={x(end)}
                    cy={y(model.value(model.periods[end] ?? '', group))}
                    fill={color}
                    r={hover === null ? 3 : 4.5}
                  />
                </g>
              );
            })}
          </Frame>
        )}
        <Tooltip format={format} hover={hover} model={model} />
      </div>
      <Legend model={model} />
    </div>
  );
}

/** A column from its top, rounded at its data end only. */
function columnPath(left: number, top: number, width: number, bottom: number, radius: number) {
  const right = left + width;
  return `M${left},${bottom}V${top + radius}Q${left},${top} ${left + radius},${top}H${right - radius}Q${right},${top} ${right},${top + radius}V${bottom}Z`;
}

/** Columns per period, stacked by group; or, without periods, one bar per group. */
export function BarChart({ rows, format }: { rows: SeriesRow[]; format: ValueFormat }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<Hover>(null);
  const model = seriesModel(rows);
  if (model.periods.length <= 1 && (model.periods[0] ?? '') === '')
    return <Bars format={format} model={model} />;
  const totals = model.periods.map((period) =>
    model.groups.reduce((sum, group) => sum + model.value(period, group), 0),
  );
  const scale = ticks(Math.max(0, ...totals));
  const top = scale.at(-1) ?? 1;
  const inner = Math.max(1, width - PAD.left - PAD.right);
  const band = inner / Math.max(1, model.periods.length);
  const thickness = Math.max(2, Math.min(22, band * 0.62));
  const x = (index: number) => PAD.left + band * index + band / 2;
  const height = (value: number) => (HEIGHT - PAD.top - PAD.bottom) * (value / top);
  return (
    <div className="widget-chart">
      <div ref={ref} className="widget-plot" onPointerLeave={() => setHover(null)}>
        {width > 0 && (
          <Frame format={format} model={model} scale={scale} width={width} x={x}>
            {model.periods.map((period, index) => {
              let base = HEIGHT - PAD.bottom;
              // Bottom up: the largest group at the base.
              const stacked = model.groups.filter((group) => model.value(period, group) > 0);
              const left = x(index) - thickness / 2;
              return (
                <g
                  key={period}
                  onPointerEnter={() =>
                    setHover({ index, x: x(index), flip: x(index) > width / 2 })
                  }
                >
                  <rect
                    fill="transparent"
                    height={HEIGHT - PAD.bottom - PAD.top}
                    width={band}
                    x={x(index) - band / 2}
                    y={PAD.top}
                  />
                  {stacked.length === 0 && (
                    <rect
                      className="widget-empty-column"
                      height={2}
                      rx={1}
                      width={thickness}
                      x={left}
                      y={HEIGHT - PAD.bottom - 2}
                    />
                  )}
                  {stacked.map((group, at) => {
                    const segmentTop = base - height(model.value(period, group));
                    // Segments are separated by a 2px gap of the surface.
                    const bottom = base - (at === 0 ? 0 : 2);
                    const radius =
                      at === stacked.length - 1 ? Math.max(0, Math.min(4, bottom - segmentTop)) : 0;
                    base = segmentTop;
                    return (
                      <path
                        key={group}
                        d={columnPath(left, segmentTop, thickness, bottom, radius)}
                        fill={`var(--series-${model.slot(group)})`}
                        opacity={hover === null || hover.index === index ? 1 : 0.45}
                      />
                    );
                  })}
                </g>
              );
            })}
          </Frame>
        )}
        <Tooltip format={format} hover={hover} model={model} />
      </div>
      <Legend model={model} />
    </div>
  );
}

/** One thin bar per group, largest first, on a faint track. */
function Bars({ model, format }: { model: SeriesModel; format: ValueFormat }) {
  const period = model.periods[0] ?? '';
  const max = Math.max(0, ...model.groups.map((group) => model.value(period, group)));
  return (
    <ul className="widget-bars">
      {model.groups.map((group) => {
        const value = model.value(period, group);
        return (
          <li key={group} title={`${group}: ${formatValue(value, format, false)}`}>
            <span className="widget-bar-label">{group || '—'}</span>
            <span className="widget-bar-track">
              <span
                className="widget-bar"
                style={{ width: `${max === 0 ? 0 : (value / max) * 100}%` }}
              />
            </span>
            <span className="widget-bar-value">{formatValue(value, format)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A stat's trend over its range: how its value built up (the running total of its periods) when
 * it is their sum, else the periods' own values (a median, a rate), from their lowest to highest.
 */
export function sparklinePoints(
  values: readonly number[],
  value: number,
  width: number,
  height: number,
): [number, number][] {
  if (values.length < 2) return [];
  const sum = values.reduce((total, entry) => total + entry, 0);
  const cumulative = Math.abs(sum - value) <= Math.max(1e-9, Math.abs(value) * 1e-6);
  let total = 0;
  const series = cumulative
    ? values.map((entry) => {
        total += entry;
        return total;
      })
    : [...values];
  const low = cumulative ? Math.min(0, ...series) : Math.min(...series);
  const span = Math.max(...series) - low || 1;
  return series.map((entry, index): [number, number] => [
    1 + ((width - 2) * index) / (values.length - 1),
    height - 2 - (height - 4) * ((entry - low) / span),
  ]);
}

function Sparkline({ values, value }: { values: number[]; value: number }) {
  const width = 72;
  const height = 30;
  const points = sparklinePoints(values, value, width, height);
  if (points.length === 0) return null;
  return (
    <svg aria-hidden="true" className="widget-sparkline" height={height} width={width}>
      <path d={smoothPath(points)} fill="none" strokeLinecap="round" strokeWidth={1.6} />
    </svg>
  );
}

/** The headline number, its change from the range before, and its trend. */
export function Stat({
  rows,
  previous,
  trend,
  format,
  better,
  range,
}: {
  rows: SeriesRow[];
  previous: number | undefined;
  trend: number[] | undefined;
  format: ValueFormat;
  better: WidgetDeclaration['better'];
  range: string | undefined;
}) {
  const value = rows.reduce((sum, row) => sum + row.value, 0);
  const change = previous === undefined || previous === 0 ? null : (value - previous) / previous;
  const tone =
    change === null || change === 0 || better === 'none'
      ? 'neutral'
      : change > 0 === (better !== 'down')
        ? 'good'
        : 'bad';
  return (
    <div className="widget-stat">
      <div className="widget-stat-main">
        <span className="widget-stat-value">{formatValue(value, format)}</span>
        {change !== null && (
          <span className="widget-stat-change" data-tone={tone}>
            {change >= 0 ? '↑' : '↓'}{' '}
            {new Intl.NumberFormat(undefined, {
              style: 'percent',
              maximumFractionDigits: 0,
            }).format(Math.abs(change))}
            {range && <small>vs. prior {range.replace(/^(\d+)([dwmy])$/, '$1 $2')}</small>}
          </span>
        )}
        {previous === 0 && value > 0 && (
          <span className="widget-stat-change" data-tone="neutral">
            New this period
          </span>
        )}
      </div>
      {trend !== undefined && <Sparkline value={value} values={trend} />}
    </div>
  );
}

/** Keys for records, which have no id of their own: their content, repeated ones numbered. */
function recordKeys(records: readonly Record<string, Cell>[]): string[] {
  const seen = new Map<string, number>();
  return records.map((record) => {
    const content = JSON.stringify(record);
    const count = (seen.get(content) ?? 0) + 1;
    seen.set(content, count);
    return `${content}#${count}`;
  });
}

/**
 * Records as a table; with `link`, the field holding each record's URL links its first column
 * instead of being a column of its own.
 */
export function RecordsTable({
  columns: all,
  records,
  format,
  link,
}: {
  columns: string[];
  records: Record<string, Cell>[];
  format: ValueFormat;
  link?: string | undefined;
}) {
  const keys = recordKeys(records);
  const columns = all.filter((column) => column !== link);
  return (
    <div className="widget-table">
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                className={typeof records[0]?.[column] === 'number' ? 'widget-number' : undefined}
                scope="col"
              >
                {columnTitle(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <tr key={keys[index]}>
              {columns.map((column) => {
                const value = record[column] ?? null;
                const text = formatCell(value, format, column);
                return (
                  <td
                    key={column}
                    className={typeof value === 'number' ? 'widget-number' : undefined}
                  >
                    {column === columns[0] &&
                    link !== undefined &&
                    typeof record[link] === 'string' &&
                    /^https:\/\//.test(String(record[link])) ? (
                      <a href={String(record[link])} rel="noreferrer" target="_blank">
                        {text}
                      </a>
                    ) : typeof value === 'string' && /^https:\/\//.test(value) ? (
                      <a href={value} rel="noreferrer" target="_blank">
                        {value.replace(/^https:\/\/(www\.)?/, '')}
                      </a>
                    ) : (
                      text
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Records as lines: the first column, the others muted, linked when a field says where. */
export function RecordsList({
  columns,
  records,
  link,
  format,
}: {
  columns: string[];
  records: Record<string, Cell>[];
  link: string | undefined;
  format: ValueFormat;
}) {
  const [first, ...rest] = columns.filter((column) => column !== link);
  const keys = recordKeys(records);
  return (
    <ul className="widget-list">
      {records.map((record, index) => {
        const href = link === undefined ? null : record[link];
        const title = first === undefined ? '' : formatCell(record[first] ?? null, format, first);
        return (
          <li key={keys[index]}>
            {typeof href === 'string' && /^https:\/\//.test(href) ? (
              <a href={href} rel="noreferrer" target="_blank">
                {title}
              </a>
            ) : (
              <span>{title}</span>
            )}
            <small>
              {rest
                .flatMap((column) => {
                  const value = record[column] ?? null;
                  return value === null || value === '' ? [] : [formatCell(value, format, column)];
                })
                .join(' · ')}
            </small>
          </li>
        );
      })}
    </ul>
  );
}
